/**
 * Shell de rutas del panel.
 *
 * Cada ruta se envuelve con su permiso: una ruta a la que el rol no tiene
 * acceso muestra el aviso en lugar de disparar un 403, que haría parecer roto
 * el sistema.
 */

import { useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ChangePasswordPage, Forbidden, LoginPage, Shell } from './components/Shell';
import { AssignmentsPage } from './pages/Assignments';
import { CartographyPage } from './pages/Cartography';
import { DashboardPage } from './pages/Dashboard';
import { FramePage } from './pages/Frame';
import { InstrumentPage } from './pages/Instrument';
import { InterviewsPage } from './pages/Interviews';
import { AuditPage, CodingPage, ExportPage, UsersPage } from './pages/Misc';
import { DataUsagePage } from './pages/DataUsage';
import { BadgesPage } from './pages/Badges';
import { ResultsPage } from './pages/Results';
import { WorkloadPage } from './pages/Workload';
import { QualityPage } from './pages/Quality';
import { useAdmin } from './store';

function Guarded({ permission, children }: { permission: string; children: React.ReactNode }) {
  const can = useAdmin((state) => state.can);
  return can(permission) ? <>{children}</> : <Forbidden />;
}

export function App() {
  const { isAuthenticated, isBootstrapping, operator, bootstrap } = useAdmin();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (isBootstrapping) {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) return <LoginPage />;
  if (operator?.mustChangePassword) return <ChangePasswordPage />;

  return (
    <Shell>
      <Routes>
        <Route
          path="/"
          element={
            <Guarded permission="monitoring:read">
              <DashboardPage />
            </Guarded>
          }
        />
        <Route
          path="/asignaciones"
          element={
            <Guarded permission="assignments:read">
              <AssignmentsPage />
            </Guarded>
          }
        />
        <Route
          path="/jornada"
          element={
            <Guarded permission="monitoring:read">
              <WorkloadPage />
            </Guarded>
          }
        />
        <Route
          path="/resultados"
          element={
            <Guarded permission="monitoring:read">
              <ResultsPage />
            </Guarded>
          }
        />
        <Route
          path="/entrevistas"
          element={
            <Guarded permission="interviews:read-all">
              <InterviewsPage />
            </Guarded>
          }
        />
        <Route
          path="/calidad"
          element={
            <Guarded permission="quality:read">
              <QualityPage />
            </Guarded>
          }
        />
        <Route
          path="/codificacion"
          element={
            <Guarded permission="coding:read">
              <CodingPage />
            </Guarded>
          }
        />
        <Route
          path="/marco"
          element={
            <Guarded permission="frame:read">
              <FramePage />
            </Guarded>
          }
        />
        <Route
          path="/cartografia"
          element={
            <Guarded permission="cartography:read">
              <CartographyPage />
            </Guarded>
          }
        />
        <Route
          path="/instrumento"
          element={
            <Guarded permission="instrument:read">
              <InstrumentPage />
            </Guarded>
          }
        />
        <Route
          path="/usuarios"
          element={
            <Guarded permission="users:read">
              <UsersPage />
            </Guarded>
          }
        />
        <Route
          path="/exportacion"
          element={
            <Guarded permission="export:data">
              <ExportPage />
            </Guarded>
          }
        />
        <Route
          path="/gafetes"
          element={
            <Guarded permission="users:read">
              <BadgesPage />
            </Guarded>
          }
        />
        <Route
          path="/consumo"
          element={
            <Guarded permission="monitoring:read">
              <DataUsagePage />
            </Guarded>
          }
        />
        <Route
          path="/bitacora"
          element={
            <Guarded permission="monitoring:read">
              <AuditPage />
            </Guarded>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Shell>
  );
}
